import axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import { get, isNumber, keyBy, map, mapValues, reduce, sortBy } from 'lodash';
import { createModel, RematchRootState } from '@rematch/core';

import { Country, SearchDescription } from '@libraries/api/gen-api';
import {
  AdminInventoryAllocationResponse,
  AdminRefundResponse,
  type BannerWithID,
  ChannelPartner,
  ChannelPartnerResultSet,
  CountryResponseServiceAdmin,
  CountryResultSetServiceAdmin,
  type CustomizationResponse,
  CustomProductRedeemVirtualProduct,
  type GrantsResponse,
  MachineToken,
  MachineTokenResultSet,
  Organization,
  OrganizationCountryResultSet,
  OrganizationProductInventorySet,
  OrganizationProductResultSet,
  OrganizationsResultSet,
  OrgNameAndNetsuiteID,
  Product,
  ProductCreateParams,
  ProductResultSet,
  PurchaseOrder,
  PurchaseOrderCreateRequest,
  PurchaseOrderResultSet,
  PurchaseOrderUpdateRequest,
  ShipmentExactLimitedResponseServiceAdmin,
  ShipmentExactResultSetServiceAdmin,
  User,
} from '@libraries/api/gen-api-admin';
import { FulfillmentPartnersResponse, FulfillmentRuleResultSet } from '@libraries/api/gen-api-fulfillment';

import { getProductFilter } from '../components/UI/SearchTable/support';

import constructGetAbortController from './getAbortController';

import {
  AdminOrganizationMap,
  BreadCrumbsParamsType,
  CustomProductRedeemTiers,
  type FeatureToggles,
  OrganizationListMap,
  ProductMap,
  SearchParams,
  SearchState,
  SortDirection,
} from '../../types/types';

import { bulkSearchStr, DEFAULT_PAGE, getDefaultSearchParams } from '../helpers';

import { apiAdmin } from '../api';
import { isNotEmpty } from '../utils';
import type { RootModel } from '.';

interface ServiceConsoleSearchStates {
  purchaseOrders: SearchState<PurchaseOrder>;
  organizations: SearchState<Organization>;
  users: SearchState<User>;
  channelPartners: SearchState<ChannelPartner>;
  standardProducts: SearchState<Product>;
  virtualProducts: SearchState<Product>;
  bulkShipments: SearchState<ShipmentExactLimitedResponseServiceAdmin>;
  refunds: SearchState<AdminRefundResponse>;
  shipments: SearchState<ShipmentExactLimitedResponseServiceAdmin>;
  distributors: SearchState<AdminHigherTierOrganizationResponse>;
  resellers: SearchState<AdminHigherTierOrganizationResponse>;
  distributorRelationships: SearchState<AdminOrgRelationshipsResponse>;
  resellerRelationships: SearchState<AdminOrgRelationshipsResponse>;
  inventoryAllocations: SearchState<AdminInventoryAllocationResponse>;
  customizations: SearchState<CustomizationResponse>;
  banners: SearchState<BannerWithID>;
  machineTokens: SearchState<MachineToken>;
  grants: SearchState<GrantsResponse>;
}

type ServiceConsoleSearchStatesKeys = keyof ServiceConsoleSearchStates;

class SearchContext<TKey extends ServiceConsoleSearchStatesKeys, TResponse> {
  constructor(
    public key: TKey,
    public search: (
      offset?: number,
      limit?: number,
      sortBy?: string,
      sortDirection?: SortDirection,
      search?: string | Array<string>,
      searchField?: string | Array<string>,
      isAdvancedSearch?: boolean,
      options?: AxiosRequestConfig
    ) => AxiosPromise<TResponse>,
    public mapRows: (response: TResponse) => ServiceConsoleSearchStates[TKey]['searchResponse']
    // eslint-disable-next-line no-empty-function
  ) {}
}

const getAbortController = constructGetAbortController<
  ServiceConsoleSearchStates,
  ServiceConsoleSearchStatesKeys
>();

const emptyPo: PurchaseOrder = {
  channelpartner_id: 1,
  organization_id: '',
  organization_name: '',
  order_date: new Date().toISOString(),
  purchaseorder_number: '',
  yubicoorder_number: '',
  yubico_subsidiary: 0,
  is_post_pay: true,
  is_tax_exempt: false,
  is_vat_valid: false,
  vat_number: '',
  is_override_shipping: false,
  override_shipping_charge: 0,
  override_shipping_key_limit: 0,
  key_contact: '',
  key_contact_email: '',
  closing_street_line1: '',
  closing_street_line2: '',
  closing_street_line3: '',
  closing_city: '',
  closing_region: '',
  closing_country_code_2: 'US',
  closing_postal_code: '',
  key_contact_phone_number: '',
  shipping_credit_purchased: 0,
  tax_credit_purchased: 0,
  vat_credit_purchased: 0,
  order_items: [],
  purchaseorder_history: [],
  channelpartner_name: '',
  channelpartner_netsuite_customer_id: '',
  reseller: null,
  distributor: null,
  allow_automated_shipments: true,
};
export interface OrgInfo extends OrgNameAndNetsuiteID {
  id: string;
}

export interface ServiceConsoleStateType {
  organizations: AdminOrganizationMap;
  organizationsList: Array<OrgInfo>;
  totalShipments: number;
  totalUsers: number;
  totalPOs: number;
  totalOrganizations: number;
  productResultSet: ProductResultSet;
  countryResultSet: CountryResponseServiceAdmin[];
  machineTokenResultSet: MachineTokenResultSet;
  recentShipments?: ShipmentExactResultSetServiceAdmin;
  recentPurchaseOrders?: PurchaseOrderResultSet;
  inventory?: OrganizationProductInventorySet;
  organizationProducts: OrganizationProductResultSet;
  organizationCountries?: OrganizationCountryResultSet;
  customProductRedeems?: Array<CustomProductRedeemVirtualProduct>;
  customProductRedeemTiers?: CustomProductRedeemTiers;
  channelPartnersResult?: ChannelPartnerResultSet;
  fulfillmentRulesResult?: FulfillmentRuleResultSet;
  fulfillmentPartnersResult?: FulfillmentPartnersResponse;
  purchaseOrder?: PurchaseOrder;
  channelPartnersResultSet?: ChannelPartnerResultSet;
  breadCrumbs?: BreadCrumbsParamsType;
  search: ServiceConsoleSearchStates;
  showDeletedAllocations: boolean;
  customizations: Array<CustomizationResponse>;
  featureTogglesList: Array<{ name: FeatureToggles; enabled: boolean }>;
  featureToggles: Record<FeatureToggles, boolean>;
  organizationMap: OrganizationListMap;
  productMap: ProductMap;
}

export const serviceConsoleInitialState: ServiceConsoleStateType = {
  organizations: {},
  organizationsList: [],
  totalShipments: 0,
  totalUsers: 0,
  totalPOs: 0,
  totalOrganizations: 0,
  machineTokenResultSet: {
    count: 0,
    total_count: 0,
    machine_tokens: [],
  },
  showDeletedAllocations: false,
  productResultSet: {
    count: 0,
    total_count: 0,
    products: [],
  },
  recentShipments: {
    count: 0,
    total_count: 0,
    shipments: [],
  },
  recentPurchaseOrders: {
    count: 0,
    total_count: 0,
    purchaseorders: [],
  },
  inventory: {},
  customProductRedeems: [],
  organizationProducts: {},
  organizationCountries: {
    count: 0,
    total_count: 0,
    organization_countries: [],
  },
  countryResultSet: [],
  customProductRedeemTiers: {},
  channelPartnersResult: {
    channelpartners: [],
    total_count: 0,
    count: 0,
  },
  fulfillmentRulesResult: {
    count: 0,
    total_count: 0,
    fulfillment_rules: [],
  },
  fulfillmentPartnersResult: {
    count: 0,
    total_count: 0,
    fulfillment_partners: [],
  },
  purchaseOrder: emptyPo,
  breadCrumbs: {
    resellerCustomerDashboard: {},
  },
  customizations: [],
  featureTogglesList: [],
  featureToggles: {
    FEExcludeColumnsCSV: false,
    FECustomization: false,
    FECustomizationPhase2: false,
    FENotifications: false,
    FENetSuiteSync: false,
    EndUserPortal: false,
    Passwordless: false,
    PasswordlessCompliance: false,
    FEPermissions: false,
    FENetSuiteIDAddProductFields: false,
  },
  organizationMap: {},
  productMap: {},
  search: {
    purchaseOrders: {
      searchParams: {
        sortBy: 'order_date',
        ...getDefaultSearchParams(),
      },
    },
    organizations: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    machineTokens: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    users: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    channelPartners: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    inventoryAllocations: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    standardProducts: {
      searchParams: {
        sortBy: 'product_name',
        ...getDefaultSearchParams(),
      },
    },
    virtualProducts: {
      searchParams: {
        sortBy: 'product_name',
        ...getDefaultSearchParams(),
      },
    },
    bulkShipments: {
      searchParams: {
        sortBy: 'shipment_request_date',
        ...getDefaultSearchParams(),
      },
    },
    refunds: {
      searchParams: {
        sortBy: 'shipment_id',
        ...getDefaultSearchParams(),
      },
    },
    shipments: {
      searchParams: {
        sortBy: 'shipment_request_date',
        ...getDefaultSearchParams(),
      },
    },
    distributors: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    resellers: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    distributorRelationships: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    resellerRelationships: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    customizations: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    banners: {
      searchParams: {
        sortDirection: 'DESC',
        page: 0,
        pageSize: 100,
      },
    },
    grants: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
  },
};

export const serviceConsole = createModel<RootModel>()({
  state: serviceConsoleInitialState,
  reducers: {
    adminOrganizations: (state: ServiceConsoleStateType, payload: OrganizationsResultSet) => ({
      ...state,
      totalOrganizations: payload.total_count,
      organizations: payload.organizations.reduce(
        (acc: AdminOrganizationMap, v: Organization): AdminOrganizationMap => {
          acc[v.id] = v;

          return acc;
        },
        {}
      ),
    }),
    toggleShowDeletedAllocations: (state: ServiceConsoleStateType) => ({
      ...state,
      showDeletedAllocations: !state.showDeletedAllocations,
    }),
    setOrganizationsList: (state: ServiceConsoleStateType, payload: Array<OrgInfo>) => ({
      ...state,
      totalOrganizations: payload?.length,
      organizationsList: [...payload],
    }),
    adminTotalShipments: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      totalShipments: payload,
    }),
    adminTotalUsers: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      totalUsers: payload,
    }),
    adminTotalPOs: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      totalPOs: payload,
    }),
    adminRecentPurchaseOrders: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      recentPurchaseOrders: payload,
    }),
    adminRecentShipments: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      recentShipments: payload,
    }),
    adminProductResultSet: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      productResultSet: payload,
      productMap: keyBy(payload.products, 'product_id'),
    }),
    adminCountryResultSet: (state: ServiceConsoleStateType, payload: CountryResultSetServiceAdmin) => ({
      ...state,
      countryResultSet: payload.countries.sort((a: Country, b: Country) =>
        a.country_name > b.country_name ? 1 : -1
      ),
    }),
    adminCustomProductRedeems: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      customProductRedeems: payload,
    }),
    adminCustomProductRedeemTiers: (state: ServiceConsoleStateType, payload: CustomProductRedeemTiers) => ({
      ...state,
      customProductRedeemTiers: payload,
    }),
    adminChannelPartners: (state: ServiceConsoleStateType, payload: ChannelPartnerResultSet) => ({
      ...state,
      channelPartnersResult: payload,
    }),
    adminOrganization: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      organization: payload,
    }),
    adminOrganizationProducts: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      organizationProducts: payload,
    }),
    adminOrganizationCountries: (state: ServiceConsoleStateType, payload: OrganizationCountryResultSet) => ({
      ...state,
      organizationCountries: {
        ...payload,
        organization_countries: payload.organization_countries.sort((a, b) =>
          a.country_name > b.country_name ? 1 : -1
        ),
      },
    }),
    adminShipment: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      shipment: payload,
    }),
    adminPurchaseOrder: (state: ServiceConsoleStateType, payload: PurchaseOrder) => ({
      ...state,
      purchaseOrder: payload,
    }),
    adminFulfillmentRules: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      fulfillmentRulesResult: payload,
    }),
    adminFulfillmentPartners: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      fulfillmentPartnersResult: payload,
    }),
    adminCustomizations: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      customizations: payload,
    }),
    adminOrganizationMap: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      organizationMap: payload ?? {},
    }),
    resetState: () => ({
      ...serviceConsoleInitialState,
    }),
    setSearchParams: (
      state,
      payload: {
        key: ServiceConsoleSearchStatesKeys;
        searchParams: Partial<SearchParams>;
      }
    ) => {
      const getPage = () => {
        // If the search field changed, reset the page
        if (isNotEmpty(payload.searchParams.search)) {
          return DEFAULT_PAGE;
        }
        if (isNumber(payload.searchParams.page)) {
          return payload.searchParams.page;
        }

        return state.search[payload.key].searchParams.page;
      };

      return {
        ...state,
        search: {
          ...state.search,
          [payload.key]: {
            ...state.search?.[payload.key],
            searchParams: {
              ...state.search?.[payload.key]?.searchParams,
              ...payload.searchParams,
              page: getPage(),
            },
          },
        },
      };
    },
    setSearchDescription: (
      state,
      payload: {
        key: ServiceConsoleSearchStatesKeys;
        searchDescription: SearchDescription;
      }
    ) => ({
      ...state,
      search: {
        ...state.search,
        [payload.key]: {
          ...state.search?.[payload.key],
          searchDescription: {
            ...state.search?.[payload.key]?.searchDescription,
            ...payload.searchDescription,
          },
        },
      },
    }),
    setSearchResponse: <TKey extends ServiceConsoleSearchStatesKeys>(
      state: ServiceConsoleStateType,
      payload: {
        key: TKey;
        searchResponse: ServiceConsoleSearchStates[TKey]['searchResponse'];
      }
    ) => ({
      ...state,
      search: {
        ...state.search,
        [payload.key]: {
          ...state.search?.[payload.key],
          searchResponse: {
            ...state.search?.[payload.key]?.searchResponse,
            ...payload.searchResponse,
          },
        },
      },
    }),
    setEmptySearchResponse: (state, payload: ServiceConsoleSearchStatesKeys) => ({
      ...state,
      search: {
        ...state.search,
        [payload]: {
          ...state.search?.[payload],
          searchResponse: {
            rows: [],
            totalCount: 0,
            responseCount: 0,
          },
        },
      },
    }),
    adminCustomBreadCrumbLabel: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      breadCrumbs: {
        ...state.breadCrumbs,
        ...payload,
      },
    }),
    setFeatureToggles: (state, payload) => ({
      ...state,
      featureToggles: reduce(
        payload,
        (featureToggles, { name, enabled }) => ({
          ...featureToggles,
          [name]: enabled,
        }),
        {} as Record<FeatureToggles, boolean>
      ),
      featureTogglesList: payload as Array<{ name: FeatureToggles; enabled: boolean }>,
    }),
    setFeatureToggle: (state: ServiceConsoleStateType, payload) => ({
      ...state,
      featureToggles: {
        ...state.featureToggles,
        [payload.name]: payload.enabled,
      },
      featureTogglesList: map(state.featureTogglesList, (toggle) => ({
        ...toggle,
        enabled: payload.name === toggle.name ? payload.enabled : toggle.enabled,
      })),
    }),
  },
  effects: (dispatch) => ({
    async adminGetAllOrganizations() {
      const res = await apiAdmin.OrganizationsApi.getAllOrganizations(0, 0);
      if (res.status === 200) {
        dispatch.serviceConsole.adminOrganizations(res.data);
        dispatch.serviceConsole.setOrganizationsList(
          sortBy(get(res, ['data', 'organizations'], []), ['name'])
        );
      }
    },
    async adminGetAllOrganizationsList() {
      const res = await apiAdmin.OrganizationsApi.listAllOrganizations();
      if (res.status === 200) {
        dispatch.serviceConsole.adminOrganizationMap(res.data);
        dispatch.serviceConsole.setOrganizationsList(
          sortBy(
            mapValues(get(res, ['data'], {}), (val: OrgNameAndNetsuiteID, key) => {
              return { ...val, id: key };
            }),
            ['name']
          )
        );
      }
    },
    async adminGetAllCountries() {
      const res = await apiAdmin.CountriesApiAdmin.getCountries(0, 0);
      if (res.status === 200) {
        dispatch.serviceConsole.adminCountryResultSet(res.data);
      }
    },
    async getTotalShipments() {
      const res = await apiAdmin.ShipmentsApiAdmin.adminGetShipments(0, 1);
      if (res.status === 200) {
        dispatch.serviceConsole.adminTotalShipments(res.data?.total_count);
      }
    },
    async getTotalUsers() {
      const res = await apiAdmin.UsersApi.getUsers(0, 1);
      if (res.status === 200) {
        dispatch.serviceConsole.adminTotalUsers(res.data?.total_count);
      }
    },
    async getTotalPOs() {
      const res = await apiAdmin.PurchaseOrdersApi.getPurchaseOrders(0, 1);
      if (res.status === 200) {
        dispatch.serviceConsole.adminTotalPOs(res.data?.total_count);
      }
    },
    async getAllProducts() {
      const res = await apiAdmin.ProductsApiAdmin.getAllProductsByName(0, 0);
      if (res.status === 200) {
        dispatch.serviceConsole.adminProductResultSet(res.data);
      }
    },
    async addProduct(product: ProductCreateParams): Promise<AxiosResponse> {
      const res = await apiAdmin.ProductsApiAdmin.addProduct(product);
      if (res.status === 200) {
        dispatch.serviceConsole.getAllProducts();
        return Promise.resolve(res);
      }
      return Promise.reject(res);
    },
    async getRecentPurchaseOrders(count: number = 5) {
      const res = await apiAdmin.PurchaseOrdersApi.getPurchaseOrders(0, count, 'order_date', 'DESC');
      if (res.status === 200) {
        dispatch.serviceConsole.adminRecentPurchaseOrders(res.data);
      }
    },
    async getRecentShipments(count: number = 5) {
      const res = await apiAdmin.ShipmentsApiAdmin.adminGetShipments(
        0,
        count,
        'shipment_request_date',
        'DESC'
      );
      if (res.status === 200) {
        dispatch.serviceConsole.adminRecentShipments(res.data);
        dispatch.serviceConsole.adminTotalShipments(res.data?.total_count);
      }
    },
    async getShipmentById(ID: string) {
      const res = await apiAdmin.ShipmentsApiAdmin.adminGetShipmentExactById(ID);
      if (res.status === 200) {
        dispatch.serviceConsole.adminShipment(res.data);
      }
    },
    async getPurchaseOrderById(ID: string) {
      const res = await apiAdmin.PurchaseOrdersApi.getPurchaseOrderById(ID);
      if (res.status === 200) {
        dispatch.serviceConsole.adminPurchaseOrder(res.data);
      }
    },
    async addPurchaseOrder(PO: PurchaseOrderCreateRequest) {
      const res = await apiAdmin.PurchaseOrdersApi.addPurchaseOrder(PO);
      if (res.status === 200) {
        dispatch.serviceConsole.adminPurchaseOrder(res.data);
        dispatch.serviceConsole.getTotalPOs();
      }
    },
    async updatePurcahseOrder({
      purchaseOrderId,
      PO,
    }: {
      purchaseOrderId: string;
      PO: PurchaseOrderUpdateRequest;
    }) {
      const res = await apiAdmin.PurchaseOrdersApi.updatePurchaseOrder(purchaseOrderId, PO);
      if (res.status === 200) {
        dispatch.serviceConsole.adminPurchaseOrder(res.data);
      }
    },
    async deletePurcahseOrderById(purchaseOrderId: string) {
      const res = await apiAdmin.PurchaseOrdersApi.deletePurchaseOrder(purchaseOrderId);
      if (res.status === 200) {
        dispatch.serviceConsole.getTotalPOs();
      }
    },
    async getAllChannelPartners() {
      const res = await apiAdmin.ChannelpartnersApiAdmin.getAllChannelPartners(0, 0);
      if (res.status === 200) {
        dispatch.serviceConsole.adminChannelPartners(res.data);
      }
    },
    async getFulfillmentRules() {
      const res = await apiAdmin.RulesApiAdmin.getAllFulfillmentRules();
      if (res.status === 200) {
        dispatch.serviceConsole.adminFulfillmentRules(res.data);
      }
    },
    async getFulfillmentPartners() {
      const res = await apiAdmin.PartnersApiAdmin.getFulfillmentPartners();
      if (res.status === 200) {
        dispatch.serviceConsole.adminFulfillmentPartners(res.data);
      }
    },

    async getAllActiveCustomizations() {
      const res = await apiAdmin.CustomizationsApiAdmin.adminGetCustomizations(
        0,
        0,
        undefined,
        'DESC',
        'status::exact::active',
        undefined,
        true
      );
      if (res.status === 200) {
        dispatch.serviceConsole.adminCustomizations(res.data?.customizations);
      }
    },
    /**
     * Search
     */
    async search<TKey extends ServiceConsoleSearchStatesKeys, TResponse>(
      searchContext: SearchContext<TKey, TResponse>,
      state: RematchRootState<RootModel>
    ) {
      const { page, pageSize, sortBy, sortDirection, search, searchField } =
        state.serviceConsole.search[searchContext.key].searchParams;

      const controller = getAbortController(searchContext.key);

      try {
        const response = await searchContext.search(
          Number.isInteger(page) && Number.isInteger(pageSize) ? page * pageSize : undefined,
          pageSize,
          sortBy,
          sortDirection,
          search,
          searchField,
          isNotEmpty(search) && Array.isArray(search),
          { signal: controller.signal }
        );

        dispatch.serviceConsole.setSearchResponse({
          key: searchContext.key,
          searchResponse: searchContext.mapRows(response.data),
        });
      } catch (error) {
        if (!axios.isCancel(error)) {
          dispatch.serviceConsole.setEmptySearchResponse(searchContext.key);
        }
      }
    },
    async getSearchDescription<TKey extends ServiceConsoleSearchStatesKeys>(payload: {
      key: TKey;
      request: () => AxiosPromise<SearchDescription>;
    }) {
      try {
        const response = await payload.request();

        dispatch.serviceConsole.setSearchDescription({
          key: payload.key,
          searchDescription: response.data,
        });
      } catch {
        /* empty */
      }
    },
    async getBannersSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'banners',
        request: apiAdmin.BannersApi.listBannerSearchDescription,
      });
    },
    async searchBanners() {
      await dispatch.serviceConsole.search(
        new SearchContext('banners', apiAdmin.BannersApi.adminGetBanners, (response) => ({
          rows: response.banners,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async searchCustomizations() {
      await dispatch.serviceConsole.search(
        new SearchContext(
          'customizations',
          apiAdmin.CustomizationsApiAdmin.adminGetCustomizations,
          (response) => ({
            rows: response.customizations,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async getCustomizationsSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'customizations',
        request: apiAdmin.CustomizationApiAdmin.listCustomizationSearchDescription,
      });
    },
    async getPurchaseOrdersSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'purchaseOrders',
        request: apiAdmin.PurchaseOrdersApi.listPurchaseOrdersSearchDescription,
      });
    },
    async searchPurchaseOrders() {
      await dispatch.serviceConsole.search(
        new SearchContext('purchaseOrders', apiAdmin.PurchaseOrdersApi.getPurchaseOrders, (response) => ({
          rows: response.purchaseorders,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async searchOrganizations() {
      await dispatch.serviceConsole.search(
        new SearchContext('organizations', apiAdmin.OrganizationsApi.getAllOrganizations, (response) => ({
          rows: response.organizations,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getOrganizationsSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'organizations',
        request: apiAdmin.OrganizationsApi.listOrganizationsSearchDescription,
      });
    },
    async searchMachineTokens() {
      await dispatch.serviceConsole.search(
        new SearchContext('machineTokens', apiAdmin.MachineTokenApi.getMachineTokens, (response) => ({
          rows: response.machine_tokens,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getMachineTokensSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'machineTokens',
        request: apiAdmin.MachineTokenApi.listMachineTokensSearchDescription,
      });
    },
    async searchUsers() {
      await dispatch.serviceConsole.search(
        new SearchContext('users', apiAdmin.UsersApi.getUsers, (response) => ({
          rows: response.users,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getUsersSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'users',
        request: apiAdmin.UsersApi.listUsersSearchDescription,
      });
    },
    async searchChannelPartners() {
      await dispatch.serviceConsole.search(
        new SearchContext(
          'channelPartners',
          apiAdmin.ChannelpartnersApiAdmin.getAllChannelPartners,
          (response) => ({
            rows: response.channelpartners,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async getStandardProductsSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'standardProducts',
        request: apiAdmin.ProductsApiAdmin.listAdminProductsSearchDescription,
      });
    },
    async searchStandardProducts() {
      await dispatch.serviceConsole.search(
        new SearchContext(
          'standardProducts',
          (offset, limit, sortBy, sortDirection, search, searchField, isAdvancedSearch, options) =>
            apiAdmin.ProductsApiAdmin.getAllProductsByName(
              offset,
              limit,
              sortBy,
              sortDirection ?? 'ASC',
              getProductFilter(search),
              searchField,
              true,
              options
            ),
          (response) => ({
            rows: response.products,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async searchVirtualProducts() {
      await dispatch.serviceConsole.search(
        new SearchContext(
          'virtualProducts',
          (offset, limit, sortBy, sortDirection, search, searchField, isAdvancedSearch, options) =>
            apiAdmin.ProductsApiAdmin.getAllProductsByName(
              offset,
              limit,
              sortBy,
              sortDirection,
              search,
              searchField,
              isAdvancedSearch,
              options
            ),
          (response) => ({
            rows: response.products,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async searchBulkShipments() {
      await dispatch.serviceConsole.search(
        new SearchContext(
          'bulkShipments',
          (offset, limit, sortBy, sortDirection, search, searchField, isAdvancedSearch, options) =>
            apiAdmin.ShipmentsApiAdmin.adminGetShipments(
              offset,
              limit,
              sortBy,
              sortDirection,
              bulkSearchStr,
              searchField,
              isAdvancedSearch,
              options
            ),
          (response) => ({
            rows: response.shipments,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async getRefundsSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'refunds',
        request: apiAdmin.ShipmentsApiAdmin.adminGetShipmentRefundSearchDescription,
      });
    },
    async searchRefunds() {
      await dispatch.serviceConsole.search(
        new SearchContext('refunds', apiAdmin.ShipmentsApiAdmin.adminGetAllShipmentRefunds, (response) => ({
          rows: response.refunds,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getShipmentsSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'shipments',
        request: apiAdmin.ShipmentsApiAdmin.listShipmentsSearchDescription,
      });
    },
    async searchShipments() {
      await dispatch.serviceConsole.search(
        new SearchContext('shipments', apiAdmin.ShipmentsApiAdmin.adminGetShipments, (response) => ({
          rows: response.shipments,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getDistributorsSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'distributors',
        request: apiAdmin.DistributorsApiAdmin.listAdminDistributorsSearchDescription,
      });
    },
    async searchDistributors() {
      await dispatch.serviceConsole.search(
        new SearchContext('distributors', apiAdmin.DistributorsApiAdmin.adminGetDistributors, (response) => ({
          rows: response.organizations,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getInventoryAllocationsSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'inventoryAllocations',
        request: apiAdmin.InventoryAllocationApi.adminListInventoryAllocationSearchDescription,
      });
    },
    async searchInventoryAllocations(showDeletedAllocations: boolean) {
      await dispatch.serviceConsole.search(
        new SearchContext(
          'inventoryAllocations',
          (offset, limit, sortBy, sortDirection, search, searchField, isAdvancedSearch) =>
            apiAdmin.InventoryAllocationApi.adminGetInventoryAllocations(
              offset,
              limit,
              sortBy,
              sortDirection,
              search,
              searchField,
              isAdvancedSearch,
              showDeletedAllocations
            ),
          (response) => ({
            rows: response.allocations,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async getResellersSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'resellers',
        request: apiAdmin.ResellersApiAdmin.listAdminResellersSearchDescription,
      });
    },
    async searchResellers() {
      await dispatch.serviceConsole.search(
        new SearchContext('resellers', apiAdmin.ResellersApiAdmin.adminGetResellers, (response) => ({
          rows: response.organizations,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getDistributorRelationshipsSearchDescription(id: string) {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'distributorRelationships',
        request: () => apiAdmin.DistributorsApiAdmin.listAdminDistributorRelationshipsSearchDescription(id),
      });
    },
    async searchDistributorRelationships(id: string) {
      await dispatch.serviceConsole.search(
        new SearchContext(
          'distributorRelationships',
          (offset, limit, sortBy, sortDirection, search, searchField, isAdvancedSearch, options) =>
            apiAdmin.DistributorsApiAdmin.adminGetDistributorRelationships(
              id,
              offset,
              limit,
              sortBy,
              sortDirection,
              search,
              searchField,
              isAdvancedSearch,
              options
            ),
          (response) => ({
            rows: response.relationships,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async getResellerRelationshipsSearchDescription(id: string) {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'resellerRelationships',
        request: () => apiAdmin.ResellersApiAdmin.listAdminResellerRelationshipsSearchDescription(id),
      });
    },
    async searchResellerRelationships(id: string) {
      await dispatch.serviceConsole.search(
        new SearchContext(
          'resellerRelationships',
          (offset, limit, sortBy, sortDirection, search, searchField, isAdvancedSearch, options) =>
            apiAdmin.ResellersApiAdmin.adminGetResellerRelationships(
              id,
              offset,
              limit,
              sortBy,
              sortDirection,
              search,
              searchField,
              isAdvancedSearch,
              options
            ),
          (response) => ({
            rows: response.relationships,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    adminCustomBreadCrumb(payload: { [pathname: string]: { label: string } }) {
      dispatch.serviceConsole.adminCustomBreadCrumbLabel(payload);
    },
    async getFeatureToggles() {
      const response = await apiAdmin.FeatureTogglesApi.getDefaultToggles();

      dispatch.serviceConsole.setFeatureToggles(get(response, ['data', 'toggles']));
    },
    async updateFeatureToggle({ name, enabled }) {
      dispatch.serviceConsole.setFeatureToggle({ name, enabled });

      try {
        const response = await apiAdmin.FeatureTogglesApi.updateDefaultToggle(name, { enabled });
        dispatch.serviceConsole.setFeatureToggle({ name, enabled: get(response, ['data', 'enabled']) });
      } catch (error) {
        dispatch.serviceConsole.setFeatureToggle({ name, enabled: !enabled });

        throw error;
      }
    },
    async searchGrants() {
      await dispatch.serviceConsole.search(
        new SearchContext('grants', apiAdmin.GrantsApi.getGrants, (response) => ({
          rows: response.grants,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getGrantsSearchDescription() {
      await dispatch.serviceConsole.getSearchDescription({
        key: 'grants',
        request: apiAdmin.GrantsApi.listGrantsSearchDescription,
      });
    },
  }),
});
